import { Box, Typography } from "@mui/material";
import { Col, Form, Modal, Row, Switch, message } from "antd";
import { FormInstance } from "antd/lib";
import { useEffect, useRef, useState } from "react";
import ActionButton from "../common/buttons/ActionButton";
import CancelButton from "../common/buttons/CancelButton";
import InputField from "../common/fields/InputField";
import { SystemConfigsRequest, SystemConfigsResponse } from "../../@type/SystemConfigs.types";
import { SystemConfigsService } from "../../api/systemConfigsService";

interface SystemConfigsFormValues {
  configKey: string;
  configValue: string;
  description?: string;
  status: boolean;
}

interface AddSystemConfigsModalProps {
  open: boolean;
  onCancel: () => void;
  reloadData: () => void;
  editData?: SystemConfigsResponse | null;
}

const AddSystemConfigsModal: React.FC<AddSystemConfigsModalProps> = ({
  open,
  onCancel,
  reloadData,
  editData,
}) => {
  const formRef = useRef<FormInstance<SystemConfigsFormValues>>(null);
  const [loading, setLoading] = useState(false);
  const isEdit = Boolean(editData);

  // Populate form values when the modal opens
  useEffect(() => {
    if (!open) {
      return;
    }
    formRef.current?.resetFields();
    if (editData) {
      formRef.current?.setFieldsValue({
        configKey: editData.config_key,
        configValue: editData.config_value,
        description: editData.description,
        status: editData.status === 1,
      });
    } else {
      formRef.current?.setFieldsValue({ status: true });
    }
  }, [open, editData]);

  const onFinish = (values: SystemConfigsFormValues) => {
    setLoading(true);
    // Jackson SNAKE_CASE on the server: request body keys must be snake_case
    const payload: SystemConfigsRequest = {
      config_key: values.configKey,
      config_value: values.configValue,
      description: values.description ?? "",
      status: values.status ? 1 : 0,
    };
    const request = isEdit
      ? SystemConfigsService.update(editData!.id, payload)
      : SystemConfigsService.create(payload);

    request
      .then(() => {
        message.success(
          isEdit
            ? "Configuration updated successfully!"
            : "Configuration added successfully!"
        );
        onCancel();
        reloadData();
      })
      .catch(() => {
        message.error(
          isEdit
            ? "Failed to update configuration"
            : "Failed to add configuration"
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Modal open={open} footer={null} onCancel={onCancel} width={600} destroyOnClose>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {isEdit ? "Edit System Config" : "Add System Config"}
      </Typography>
      <Form
        onFinish={onFinish}
        ref={formRef}
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 16 }}
        labelAlign="left"
        initialValues={{ status: true }}
        preserve={false}
      >
        <Row>
          <Col span={24}>
            <Form.Item
              name="configKey"
              label="Config Key"
              rules={[{ required: true, message: "Please input configuration key!" }]}
            >
              <InputField disabled={isEdit} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              name="configValue"
              label="Config Value"
              rules={[{ required: true, message: "Please input configuration value!" }]}
            >
              <InputField multiline minRows={4} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="description" label="Description">
              <InputField />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              name="status"
              label="Status"
              rules={[{ required: true, message: "Please select status!" }]}
              valuePropName="checked"
            >
              <Switch checkedChildren="ON" unCheckedChildren="OFF" />
            </Form.Item>
          </Col>
        </Row>
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 3 }}>
          <CancelButton onClick={onCancel}>Cancel</CancelButton>
          <ActionButton type="submit" loading={loading}>
            {isEdit ? "Update" : "Add"}
          </ActionButton>
        </Box>
      </Form>
    </Modal>
  );
};

export default AddSystemConfigsModal;
